import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Dumbbell, Salad, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Empty, StatusBadge } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import api, { errMsg, fmtDate } from "@/lib/api";

const STARTER = {
  workout: { days: Array.from({ length: 7 }, (_, index) => ({ name: `Day ${index + 1}`, focus: "", exercises: index === 0 ? [{ name: "", sets: 3, reps: "8-12", rest: "90s", notes: "" }] : [] })), notes: "" },
  meal: { targets: { calories: 2200, protein: 150, carbs: 220, fat: 70 }, days: [{ name: "Day 1", meals: [{ name: "Breakfast", items: "", calories: 0, protein: 0, carbs: 0, fat: 0 }] }], notes: "" },
};

function PlanCard({ plan, onDuplicate, onDelete, onRename, compact = false }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(plan.title);
  const [savingTitle, setSavingTitle] = useState(false);
  const Icon = plan.type === "workout" ? null : Salad;
  const days = plan.content?.days || [];
  const dayCount = plan.type === "workout"
    ? days.filter((day) => (day.exercises || []).some((exercise) => String(exercise.name || "").trim())).length
    : days.length;
  const saveTitle = async (event) => {
    event.preventDefault();
    const nextTitle = title.trim();
    if (!nextTitle) return toast.error("Workout name is required");
    if (nextTitle === plan.title) return setEditingTitle(false);
    setSavingTitle(true);
    const saved = await onRename(plan, nextTitle);
    setSavingTitle(false);
    if (saved) {
      setTitle(nextTitle);
      setEditingTitle(false);
    }
  };
  return (
    <div className={`rounded-lg ${compact ? "p-4 gap-2" : "p-5 gap-3"} flex flex-col fade-up ${plan.status === "ai_draft" ? "ai-draft" : "panel"}`} data-testid={`plan-card-${plan.id}`}>
      {(Icon || plan.status !== "template") && <div className="flex items-start justify-between gap-2">
        {Icon && <Icon size={18} className="text-emerald-400" />}
        {plan.status !== "template" && <StatusBadge status={plan.status} />}
      </div>}
      {editingTitle ? (
        <form onSubmit={saveTitle} className="flex min-w-0 items-center gap-1">
          <Input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} className="h-9 min-w-0 flex-1 bg-[#111827] text-xl font-display font-semibold uppercase leading-tight" aria-label="Workout template name" data-testid={`workout-template-name-${plan.id}`} />
          <Button type="submit" variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={savingTitle} aria-label="Save workout name" title="Save workout name"><Check size={15} /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={savingTitle} onClick={() => { setTitle(plan.title); setEditingTitle(false); }} aria-label="Cancel rename" title="Cancel rename"><X size={15} /></Button>
        </form>
      ) : (
        <div className="flex flex-wrap items-start gap-1">
          <Link to={`/coach/plans/${plan.id}`} className="max-w-full font-display text-xl font-semibold uppercase leading-tight hover:text-[#FF3B30]" data-testid={`plan-open-${plan.id}`}>{plan.title}</Link>
          {onRename && <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setEditingTitle(true)} aria-label="Edit workout template name" title="Edit workout template name" data-testid={`workout-template-rename-${plan.id}`}><Pencil size={14} /></Button>}
          {compact && onDuplicate && <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => onDuplicate(plan)} aria-label="Duplicate workout template" title="Duplicate workout template" data-testid={`plan-duplicate-${plan.id}`}><Copy size={14} /></Button>}
          {compact && onDelete && <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-red-400" onClick={() => onDelete(plan)} aria-label="Delete workout template" title="Delete workout template" data-testid={`plan-delete-${plan.id}`}><Trash2 size={14} /></Button>}
        </div>
      )}
      <div className="text-xs text-slate-500">{plan.type === "workout" ? `${dayCount} days/week` : `${dayCount} day(s) · ${plan.client_name ? `for ${plan.client_name}` : "library"} · ${fmtDate(plan.updated_at)}`}</div>
      {!compact && <div className="flex items-center gap-1 -ml-2">
        {onDuplicate && <Button variant="ghost" size="sm" onClick={() => onDuplicate(plan)} data-testid={`plan-duplicate-${plan.id}`}><Copy size={13} className="mr-1" />Duplicate</Button>}
        {onDelete && <Button variant="ghost" size="sm" className="text-red-400" onClick={() => onDelete(plan)} data-testid={`plan-delete-${plan.id}`}><Trash2 size={13} className="mr-1" />Delete</Button>}
      </div>}
    </div>
  );
}

function Grid({ plans, onDuplicate, onDelete, onRename, empty, compact = false }) {
  if (!plans) return <div className="eyebrow">Loading…</div>;
  if (!plans.length) return <Empty text={empty} />;
  return <div className={`grid ${compact ? "sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5" : "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"} gap-4`}>{plans.map((plan) => <PlanCard key={plan.id} plan={plan} onDuplicate={onDuplicate} onDelete={onDelete} onRename={onRename} compact={compact} />)}</div>;
}

export default function Templates() {
  const navigate = useNavigate();
  const [templates, reload] = useLoad("/plans", { kind: "templates" });
  const [drafts] = useLoad("/plans", { kind: "drafts" });
  const [tab, setTab] = useState("workout");

  const create = async (type) => {
    try {
      const { data } = await api.post("/plans", { type, title: type === "workout" ? "New workout template" : "New meal template", content: STARTER[type] });
      navigate(`/coach/plans/${data.id}`);
    } catch (error) { toast.error(errMsg(error)); }
  };
  const duplicate = async (plan) => {
    try { await api.post(`/plans/${plan.id}/duplicate`, { as_template: true }); toast.success("Template duplicated"); reload(); } catch (error) { toast.error(errMsg(error)); }
  };
  const rename = async (plan, title) => {
    try { await api.put(`/plans/${plan.id}`, { title }); toast.success("Workout template renamed"); reload(); return true; }
    catch (error) { toast.error(errMsg(error)); return false; }
  };
  const remove = async (plan) => {
    if (!window.confirm(`Delete "${plan.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/plans/${plan.id}`); toast.success("Template deleted"); reload(); } catch (error) { toast.error(errMsg(error)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Reusable programming" title="Templates" testid="templates-title">
        <Button variant="outline" onClick={() => create("meal")} data-testid="new-meal-template-btn"><Plus size={15} className="mr-1" />Meal template</Button>
        <Button onClick={() => create("workout")} data-testid="new-workout-template-btn"><Plus size={15} className="mr-1" />Workout template</Button>
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#111827] border border-[#1E293B] mb-6 flex-wrap h-auto">
          <TabsTrigger value="workout" data-testid="templates-tab-workout">Workouts</TabsTrigger>
          <TabsTrigger value="meal" data-testid="templates-tab-meal">Meal plans</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="templates-tab-drafts">AI drafts ({drafts?.length || 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="workout"><Grid plans={templates?.filter((plan) => plan.type === "workout")} onDuplicate={duplicate} onDelete={remove} onRename={rename} empty="No workout templates yet." compact /></TabsContent>
        <TabsContent value="meal"><Grid plans={templates?.filter((plan) => plan.type === "meal")} onDuplicate={duplicate} onDelete={remove} empty="No meal templates yet." /></TabsContent>
        <TabsContent value="drafts"><Grid plans={drafts} empty="No AI drafts awaiting review." /></TabsContent>
      </Tabs>
    </div>
  );
}
